export const INDEXABLE_ROBOTS =
  "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
export const NOINDEX_ROBOTS = "noindex, follow";

export type PageMeta = {
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
  canonicalPath: string;
  robots: string;
};

const CHAT_DESCRIPTION =
  "Ask FAM about care, records and appointments — from first steps to later years.";

export const CHAT_META: PageMeta = {
  title: "Chat with FAM",
  description: CHAT_DESCRIPTION,
  ogTitle: "Chat with FAM",
  ogDescription: CHAT_DESCRIPTION,
  canonicalPath: "/chat",
  robots: INDEXABLE_ROBOTS,
};

const PAGE_META: Record<string, PageMeta> = {
  "/": CHAT_META,
  "/chat": CHAT_META,
  "/signin": {
    title: "Update required — FAM",
    description: "Download or update the FAM app to continue.",
    ogTitle: "Update required — FAM",
    ogDescription: "Download or update the FAM app to continue.",
    canonicalPath: "/signin",
    robots: NOINDEX_ROBOTS,
  },
  "/login": {
    title: "Log in — FAM",
    description: "Log in or sign up to FAM on the web.",
    ogTitle: "Log in — FAM",
    ogDescription: "Log in or sign up to FAM on the web.",
    canonicalPath: "/login",
    robots: NOINDEX_ROBOTS,
  },
};

const NOINDEX_PREFIXES = [
  "/reset-password",
  "/verify-email",
  "/verification-pending",
];

export function normalizePath(pathname: string): string {
  if (!pathname || pathname === "/") {
    return "/";
  }
  return pathname.replace(/\/+$/, "") || "/";
}

export function getPageMeta(pathname: string): PageMeta {
  const path = normalizePath(pathname);
  const exact = PAGE_META[path];
  if (exact) {
    return exact;
  }

  if (NOINDEX_PREFIXES.some((prefix) => {
    const base = prefix.replace(/\/+$/, "");
    return path === base || path.startsWith(`${base}/`);
  })) {
    return {
      ...CHAT_META,
      title: "FAM",
      canonicalPath: path,
      robots: NOINDEX_ROBOTS,
    };
  }

  return {
    ...CHAT_META,
    canonicalPath: path,
  };
}

function siteOrigin(): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "https://chat.fam.life";
}

function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function applyPageMeta(meta: PageMeta) {
  const canonical = `${siteOrigin()}${meta.canonicalPath === "/" ? "/" : meta.canonicalPath}`;

  document.title = meta.title;
  setMeta("name", "description", meta.description);
  setMeta("name", "robots", meta.robots);
  setMeta("name", "googlebot", meta.robots.startsWith("noindex") ? "noindex, follow" : "index, follow");
  setMeta("property", "og:title", meta.ogTitle);
  setMeta("property", "og:description", meta.ogDescription);
  setMeta("property", "og:url", canonical);
  setMeta("name", "twitter:title", meta.ogTitle);
  setMeta("name", "twitter:description", meta.ogDescription);
  setCanonical(canonical);
}
